/**
 * Das Bild eines Projekts, überall in derselben Form.
 *
 * Ohne hochgeladenes Bild steht der Anfangsbuchstabe auf der Akzentfarbe des
 * Projekts. Das ist kein Platzhalter für später, sondern eine vollwertige
 * Darstellung: Wer fünf Projekte hat, unterscheidet sie an der Farbe genauso
 * zuverlässig wie an einem Bild.
 */
export function ProjectAvatar({
  name, accentColor, coverUrl, size = 32, radius,
}: {
  name: string;
  accentColor: string;
  coverUrl?: string | null;
  size?: number;
  radius?: number;
}) {
  const r = radius ?? Math.round(size * 0.3);

  if (coverUrl) {
    return (
      <img
        src={coverUrl}
        alt=""
        style={{
          width: size, height: size, borderRadius: r, flex: 'none',
          objectFit: 'cover', display: 'block', border: '1px solid var(--line)',
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: size, height: size, borderRadius: r, flex: 'none',
        background: avatarColor(name, accentColor), color: 'oklch(0.2 0.02 265)',
        display: 'grid', placeItems: 'center',
        fontSize: Math.max(10, Math.round(size * 0.42)), fontWeight: 700,
      }}
    >
      {(name || '?').slice(0, 1).toUpperCase()}
    </div>
  );
}

/**
 * Avatarfarbe im neuen System: immer dieselbe Helligkeit und Sättigung,
 * nur der Farbton wechselt -- aus der Projektfarbe, sonst aus dem Namen.
 * So passen alle Avatare zusammen und bleiben in Hell und Dunkel lesbar.
 */
export function avatarColor(name: string, accentColor?: string | null): string {
  return `oklch(0.76 0.12 ${hueOf(name, accentColor)})`;
}

function hueOf(name: string, hex?: string | null): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex ?? '');
  if (m) {
    const n = parseInt(m[1], 16);
    const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
    if (d > 0.04) {
      let h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
      h *= 60;
      return Math.round(h < 0 ? h + 360 : h);
    }
  }
  let hash = 0;
  for (const ch of name || '?') hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return hash % 360;
}
